import { useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import SecurityAlert from '../../../components/error/Alert';
import * as ThemeManager from '../../../services/themeManager';
import Alert from '../../common/Alert';
import NNLogo from '../../common/NNLogo';
import { submitLoginFormAction, updateEmailAction, updatePasswordAction } from '../core/actions';
import { getErrorState, getLoginFormState, isLoaded } from '../core/selectors';
import { IState, LoginFormError, LoginFormState } from '../core/types';
import './LoginForm.scss';
import PasswordForgotLink from './PasswordForgotLink';
import RegistrationLink from './RegistrationLink';
import SocialLogin from './SocialLogin';

export interface LoginFormProps {
  form: LoginFormState;
  loaded: boolean;
  error?: LoginFormError;
  onEmailChange: React.ChangeEventHandler;
  onPasswordChange: React.ChangeEventHandler;
  onSubmit: React.FormEventHandler;
}

export function LoginForm(props: LoginFormProps) {
  const { all = '', email = '', password = '' } = props.error || {};
  const { t } = useTranslation();
  // autofocus on open
  const inputEl = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (inputEl && inputEl.current) {
      inputEl.current.focus();
    }
  }, [props.loaded]);
  if (!props.loaded && all) return <SecurityAlert />;
  if (!props.loaded) {
    return null;
  }
  const invalidClassname = ' is-invalid';
  const emailClass = 'form-control' + (email ? invalidClassname : '');
  const passwordClass = 'form-control' + (password ? invalidClassname : '');
  const rootError = all ? <Alert>{t(all)}</Alert> : null;
  return (
    <ThemeManager.ThemeConsumer>
      {(theme) => (
        <div className="login-form text-center">
          {theme !== ThemeManager.Themes.Opxp && <NNLogo />}
          <div className="card">
            <h5 className="card-header">{t('Login.Title')}</h5>
            <div className="card-body">
              {rootError}
              <form onSubmit={props.onSubmit} noValidate={true}>
                <div className="form-group">
                  {theme !== ThemeManager.Themes.Opxp && <label htmlFor="emailInput">{t('Login.Email.Label')}</label>}
                  <input
                    type="email"
                    ref={inputEl}
                    className={emailClass}
                    id="emailInput"
                    onChange={props.onEmailChange}
                    value={props.form.email}
                    required={true}
                    placeholder={t('Login.Email.Placeholder')}
                  />
                  <div className="invalid-feedback">{t(email)}</div>
                </div>
                <div className="form-group">
                  {theme !== ThemeManager.Themes.Opxp && (
                    <label htmlFor="passwordInput">{t('Login.Password.Label')}</label>
                  )}
                  <input
                    type="password"
                    className={passwordClass}
                    id="passwordInput"
                    onChange={props.onPasswordChange}
                    value={props.form.password}
                    required={true}
                    placeholder={t('Login.Password.Placeholder')}
                  />
                  <div className="invalid-feedback">{t(password)}</div>
                </div>
                <button type="submit" className="btn btn-lg btn-primary btn-block">
                  {t('Login.Submit')}
                </button>

                <PasswordForgotLink email={props.form.email} />
                <RegistrationLink theme={theme} />
                <SocialLogin theme={theme} />
              </form>
            </div>
          </div>
        </div>
      )}
    </ThemeManager.ThemeConsumer>
  );
}

export const mapStateToProps = (state: IState) => {
  const form = getLoginFormState(state);
  const loaded = isLoaded(state);
  const error = getErrorState(state);
  return {
    error,
    form,
    loaded,
  };
};

export const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(updateEmailAction(event.target.value));
    },
    onPasswordChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(updatePasswordAction(event.target.value));
    },
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      dispatch(submitLoginFormAction());
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
